/**
 * Monday move review (ruling 2026-10-05): the PA manager's exception queue
 * over the MONDAY_DRIVEN_PA_MOVE ledger. Mirrors
 * backend/src/services/advocate/mondayMoveQueue.js. Manager only; the server
 * refuses everyone else.
 */
import { api } from '../../services/api';
import type { ApiResult } from './advocateApi';

export type MoveClass = 'CONSISTENT' | 'SUPERSEDED' | 'FACILITY_MISMATCH' | 'NATIVE_ACTION_OVERRIDDEN' | 'IDENTITY_CONFLICT' | 'REVIEW_REQUIRED';
export type QueuedClass = 'FACILITY_MISMATCH' | 'NATIVE_ACTION_OVERRIDDEN' | 'IDENTITY_CONFLICT' | 'REVIEW_REQUIRED';
export const QUEUED_CLASSES: readonly QueuedClass[] = ['FACILITY_MISMATCH', 'NATIVE_ACTION_OVERRIDDEN', 'IDENTITY_CONFLICT', 'REVIEW_REQUIRED'];
export type MoveKind = 'MONDAY_FOLLOW' | 'MONDAY_FOLLOW_SIBLING' | 'TRANSFER' | 'PROJECTOR';
export type MoveStatus = 'OPEN' | 'ACCEPTED' | 'REASSIGNED';
export type StatusFilter = 'OPEN' | 'DECIDED';

export const CLASS_LABEL: Record<MoveClass, string> = {
  CONSISTENT: 'Consistent',
  SUPERSEDED: 'Superseded',
  FACILITY_MISMATCH: 'Not the building’s PA',
  NATIVE_ACTION_OVERRIDDEN: 'Overrode eMerge work',
  IDENTITY_CONFLICT: 'Identity in doubt',
  REVIEW_REQUIRED: 'Needs a look',
};
export const CLASS_WHAT: Record<QueuedClass, string> = {
  FACILITY_MISMATCH: 'A PA a manager confirmed for a building on record for this resident (then or now) is not the advocate Monday chose or not whoever holds it, or Monday chose an advocate who does not cover the building at all.',
  NATIVE_ACTION_OVERRIDDEN: 'The advocate Monday took the case from had already worked it in eMerge, or a manager or the round-robin had placed it.',
  IDENTITY_CONFLICT: 'The Monday user maps to more than one person (or to nobody), or the resident record is in doubt.',
  REVIEW_REQUIRED: 'Something else is off: the records name more than one building (or none), a building on record was merged into another, nobody covers the building, the match rests only on an unconfirmed suggestion or on a PA confirmed after the move, Monday moved it again after the review window, the advocate has left, or the holder changed with no eMerge placement.',
};
export const KIND_LABEL: Record<MoveKind, string> = {
  MONDAY_FOLLOW: 'Board reassignment followed',
  MONDAY_FOLLOW_SIBLING: 'Moved with the resident’s other case',
  TRANSFER: 'Board assignee transferred in',
  PROJECTOR: 'Board sync changed the holder',
};
export const REASON_LABEL: Record<string, string> = {
  PATIENT_MISSING: 'The case has no resident record',
  PATIENT_RETIRED: 'The resident record was retired into another',
  IDENTITY_REVIEW_OPEN: 'An identity review is open on the resident',
  RESIDENT_MERGED_AFTER_MOVE: 'The resident’s records were merged after the move',
  MONDAY_USER_AMBIGUOUS: 'The Monday user maps to more than one person',
  MONDAY_USER_UNMAPPED: 'The Monday user maps to nobody now',
  MONDAY_USER_MAPS_ELSEWHERE: 'The Monday user now maps to someone else',
  LATER_NATIVE_PLACEMENT: 'Placed in eMerge after the move',
  FROM_ADVOCATE_NATIVE_ACTIVITY: 'The previous advocate had worked it in eMerge',
  PRIOR_MANAGER_PLACEMENT: 'A manager had placed it in eMerge',
  PRIOR_ROUND_ROBIN_PLACEMENT: 'The round-robin had placed it',
  PRIOR_RESIDENT_SCOPE_AUTOMATION_PLACEMENT: 'The round-robin had placed the resident',
  LATER_MONDAY_MOVE: 'Monday moved it again later',
  MONDAY_MOVED_AFTER_WINDOW: 'Monday moved it again after the review window; that later move is not in this review',
  LATER_MONDAY_MOVE_NOT_IN_LEDGER: 'Monday moved it again, in a kind of move not loaded into this review yet',
  MOVE_PARTIES_UNKNOWN: 'Who it moved from and to cannot be read',
  MOVED_TO_NOBODY: 'Monday took it away from everyone',
  HOLDER_DIFFERS_NO_NATIVE_PLACEMENT: 'Someone else holds it now, with no eMerge placement',
  CONFIRMED_PA_CONTRADICTS: 'A PA a manager confirmed for a building on record is not the advocate Monday chose, or not whoever holds it',
  BUILDING_SOURCES_DISAGREE: 'The records name more than one building for this resident',
  NO_BUILDING_SOURCE: 'No record names a building for this resident',
  RESIDENT_UNRESOLVED: 'The resident’s merged records cannot be followed to one record',
  MERGE_DATA_INCONSISTENT: 'The resident’s merge records disagree about which record was kept',
  TO_NOT_FACILITY_PA: 'Not the building’s PA',
  NO_FACILITY_PA: 'Nobody covers the building',
  TO_ADVOCATE_NOT_ACTIVE: 'That advocate no longer holds an active role',
  FACILITY_PA_UNCONFIRMED_BEFORE_MOVE: 'Matches the building’s PA only by an unconfirmed suggestion (drawn partly from Monday’s own moves)',
  FACILITY_PA_UNCONFIRMED_AFTER_MOVE: 'Matches the building’s PA only by an unconfirmed suggestion made after the move',
  // Owner rulings, 2026-10-07.
  FACILITY_MERGED: 'A building on record was merged into another building; both are shown',
  FACILITY_MERGE_CHAIN_UNRESOLVED: 'A building’s merge records loop or run too deep to follow to one building',
  PA_CONFIRMED_AFTER_MOVE: 'The building’s PA was confirmed only after the move, so it does not show who covered the building then',
};
/** Where a candidate building is named (backend mondayMoveLedger BUILDING_SOURCES). */
export const BUILDING_SOURCE_LABEL: Record<string, string> = {
  RELATIONSHIP_AT_MOVE: 'building record at the move',
  RELATIONSHIP_NOW: 'building record now',
  CASE_FACILITY: 'the case',
  PATIENT_FACILITY: 'the resident’s profile',
  RETIRED_RELATIONSHIP_AT_MOVE: 'a merged duplicate’s building record at the move',
  RETIRED_RELATIONSHIP_NOW: 'a merged duplicate’s building record now',
  RETIRED_PATIENT_FACILITY: 'a merged duplicate’s profile',
  MERGED_FACILITY: 'the building another building on record was merged into',
};
export type BuildingDecision = 'ONE_BUILDING' | 'SOURCES_DISAGREE' | 'NO_SOURCE' | 'RESIDENT_UNRESOLVED';
/** Said above the building table when the records do not agree on one building. */
export const BUILDING_DECISION_NOTE: Record<BuildingDecision, string | null> = {
  ONE_BUILDING: null,
  SOURCES_DISAGREE: 'The records name more than one building for this resident. Every building and its PA are below.',
  NO_SOURCE: 'No record names a building for this resident.',
  RESIDENT_UNRESOLVED: 'This resident’s merged records could not be followed to one record.',
};

/** Why a resident's records did not resolve to one kept record (backend mondayMoveLedger resolveResident). */
export const RESIDENT_UNRESOLVED_LABEL: Record<string, string> = {
  NO_RESIDENT: 'The case has no resident record.',
  RECORD_NOT_FOUND: 'A resident record could not be read.',
  MERGE_CHAIN_LOOPS: 'The resident’s merge records loop back on themselves.',
  MERGE_CHAIN_TOO_DEEP: 'The resident’s merge records run more than ten merges deep.',
  MERGE_DATA_INCONSISTENT: 'The resident’s two merge records (the link on each record and the merge log) disagree.',
};
/** One way the two merge records disagree. */
export const MERGE_INCONSISTENCY_LABEL: Record<string, string> = {
  LINK_MERGES_DISAGREE: 'its link and its merge log name different kept records',
  LINK_WITHOUT_MERGE_ROW: 'it is linked to a kept record, but the merge log has no entry for it',
  MERGE_ROW_WITHOUT_LINK: 'the merge log retired it, but it is not linked (the merge may have been undone)',
  MORE_THAN_ONE_KEPT: 'more than one of these records is marked as the kept one',
};

/** How the Monday user behind a move is known. */
export const MONDAY_USER_BASIS_LABEL: Record<string, string> = {
  FOLLOW_KEY: 'from the reassignment record',
  TRANSFER_DETAIL: 'from the transfer record',
  CASE_SOURCE_COLUMN: 'from the case’s board column, unchanged since the move',
};
/** Why the Monday user behind a move is not known. */
export const MONDAY_USER_NOT_RECORDED_LABEL: Record<string, string> = {
  BOARD_COLUMN_CHANGED_LATER: 'the board sync records no user, and the board column has changed since',
  UNEXPLAINED_HOLDER_CHANGE_LATER: 'the board sync records no user, and the case changed hands later without a record',
  BOARD_COLUMN_EMPTY: 'the board column is empty',
  CASE_NOT_FOUND: 'the case could not be read',
  FOLLOW_KEY_UNREADABLE: 'the reassignment record does not name one',
  TRANSFER_DETAIL_MISSING: 'the transfer record does not name one',
  KIND_RECORDS_NO_BOARD_USER: 'this kind of move records none',
};
export const PLACEMENT_LABEL: Record<string, string> = {
  MANAGER: 'a manager', ROUND_ROBIN: 'the round-robin', RESIDENT_SCOPE_AUTOMATION: 'the round-robin (resident)', AUTOMATION: 'an automation',
  TRANSFER: 'the Monday transfer', PROJECTOR: 'the Monday sync', MONDAY_FOLLOW: 'a Monday reassignment', MONDAY_FOLLOW_SIBLING: 'a Monday reassignment (resident)',
};

export interface Person { user_id: string; name: string | null }
export interface Activity { notes: number; statusChanges: number; acts: number }
export interface Placement { eventId: string; kind: string; rule: string | null; reason: string | null; actorUserId: string | null; toUserId: string | null; at: string }
export interface MoveEvidence {
  sourceEvent?: {
    eventId: string; mondayUserId: string | null; mondayItemId: string | null;
    mondayUserBasis?: string; mondayUserNotRecorded?: string | null; identityChecked?: boolean;
  };
  priorAssignment?: Placement | null;
  laterNativePlacement?: Placement | null;
  lastLaterPlacement?: Placement | null;
  lastLaterMondayScope?: 'IN_LEDGER' | 'AFTER_WINDOW' | 'NOT_IN_LEDGER' | null;
  facility?: {
    decision?: BuildingDecision; agreement?: string; buildingChanged?: boolean;
    /** The one building's, when every source names one; else null. */
    facilityId: string | null; paAtMove: string | null; paAtMoveBasis?: string; paNow: string | null;
    paAtMoveConfirmed?: boolean | null; paNowConfirmed?: boolean | null;
  };
  activity?: { fromBefore: Activity; fromAfter: Activity; toAfter: Activity };
}
export interface BuildingPa { user_id: string; name: string | null; confirmed: boolean; basis?: string | null; source?: string | null; confirmed_after_move?: boolean }
/** One building a record names for the resident, with its PA at the move and now, and which of those contradict. */
export interface CandidateBuilding {
  facility_id: string; name: string | null; named_at_move: boolean; named_now: boolean;
  sources: { source: string; when: 'AT_MOVE' | 'NOW'; record: 'KEPT' | 'RETIRED' | 'CASE' }[];
  pa_at_move: BuildingPa | null; pa_now: BuildingPa | null;
  contradicts: { when: 'AT_MOVE' | 'NOW'; user_id: string; against: ('HOLDER' | 'TO')[] }[];
}
/** One record of the resident's merge component: its role, what its link and its merge-log entry name, and its profile. */
export interface ResidentRecord {
  patient_id: string; role: 'KEPT' | 'RETIRED'; is_case: boolean; depth: number;
  merged_into: string | null; merge_row_kept: string | null; name: string | null; building: string | null;
}
export interface MergeInconsistency {
  type: string; patient_id: string | null; link_kept: string | null; merge_row_kept: string | null; kept_patient_ids: string[] | null;
}
/** The resident's records (the merge component of the case's record) and how they resolve. */
export interface ResidentRecords {
  resolution: 'SELF' | 'FOLLOWED_MERGE' | 'UNRESOLVED' | null; unresolved: string | null;
  records: ResidentRecord[]; inconsistencies: MergeInconsistency[];
}
export interface MoveRow {
  id: string; move_kind: MoveKind; case_id: string; patient_id: string | null; resident: string;
  /** The one building every record names, or null (then `buildings` lists them). */
  facility: string | null;
  building_decision?: BuildingDecision | null;
  buildings?: CandidateBuilding[];
  resident_records?: ResidentRecords | null;
  monday_user_id: string | null; moved_at: string;
  from: { user_id: string | null; name: string | null }; to: { user_id: string | null; name: string | null };
  holder_now: { user_id: string | null; name: string | null; stage: string | null };
  classification: MoveClass; reasons: string[]; evidence: MoveEvidence;
  prior_assignment_to_name: string | null;
  status: MoveStatus; decision_basis: 'BASELINE' | 'MANAGER_ACCEPT' | 'MANAGER_REASSIGN' | 'COVERED_BY_REASSIGN' | null;
  baseline: Person | null; decided_by: Person | null; decided_at: string | null; decision_note: string | null;
}
export interface MoveQueue {
  available: boolean;
  counts: { byClass: Record<QueuedClass, { open: number; decided: number }>; open: number; decided: number; baseline: number } | null;
  items: MoveRow[]; total: number; page: number; pageSize: number;
}
export interface Decision { replayed: boolean; id: string; status: MoveStatus; baselineAdvocateUserId: string | null }

function qs(params: Record<string, string | number | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

const BASE = '/advocate/reconciliations/monday-moves';

export const mondayMovesApi = {
  queue: async (l: { cls?: QueuedClass | null; status?: StatusFilter; page?: number }): Promise<ApiResult<MoveQueue>> =>
    (await api.get(`${BASE}${qs({ class: l.cls || null, status: l.status || 'OPEN', page: l.page })}`)) as ApiResult<MoveQueue>,
  accept: async (id: string, body: { expected_holder: string | null; note?: string; idempotency_key: string }): Promise<ApiResult<Decision>> =>
    (await api.post(`${BASE}/${id}/accept`, body)) as ApiResult<Decision>,
  reassign: async (id: string, body: { advocate_user_id: string; expected_from: string | null; note?: string; idempotency_key: string }): Promise<ApiResult<Decision>> =>
    (await api.post(`${BASE}/${id}/reassign`, body)) as ApiResult<Decision>,
};
